import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  getAllCountries, 
  getSupportedCountries, 
  isServiceAvailable, 
  SUPPORTED_SERVICES 
} from '../../constants/countries';

interface CountrySelectorProps {
  /**
   * Currently selected country ISO code
   */
  value: string;
  
  /**
   * Callback when country selection changes
   * Returns ISO code rather than country name
   */
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  
  /**
   * Whether to disable the selector
   */
  disabled?: boolean;
  
  /**
   * Custom ID for the select element
   */
  id?: string;
  
  /**
   * Custom CSS class for the select element
   */
  className?: string;
  
  /**
   * Optional placeholder text
   */
  placeholder?: string;
  
  /**
   * Whether the field is required
   */
  required?: boolean;
  
  /**
   * Error message to display
   */
  error?: string;
  
  /**
   * Label for the field
   */
  label?: string;
  
  /**
   * Custom instruction text
   */
  helpText?: string;
  
  /**
   * Whether to use a large select input
   */
  isLarge?: boolean;
  
  /**
   * Filter countries list to only show those that support specific services
   * If none provided, all countries will be shown
   */
  supportedServices?: Array<keyof typeof SUPPORTED_SERVICES>;
  
  /**
   * Whether to show all countries or only those that support at least one service
   * Default is to show only supported countries
   */
  showAllCountries?: boolean;

  /**
   * Whether to show service availability indicators next to each country
   */
  showServiceIndicators?: boolean;
}

/**
 * A reusable country selector component that displays a dropdown list of countries.
 * 
 * The component uses i18n for translations and displays countries in alphabetical order.
 * It supports custom styling, accessibility features, and filtering by supported services.
 */
const CountrySelector: React.FC<CountrySelectorProps> = ({
  value,
  onChange,
  disabled = false,
  id = 'country',
  className = '',
  placeholder = '',
  required = false,
  error,
  label = '',
  helpText,
  isLarge = false,
  supportedServices,
  showAllCountries = false,
  showServiceIndicators = false
}) => {
  const { t } = useTranslation(['withdrawals']);
  
  // Generate additional CSS classes
  const selectClasses = [
    'form-select',
    isLarge ? 'form-select-lg' : '',
    error ? 'is-invalid' : '',
    className
  ].filter(Boolean).join(' ');
  
  // Get the appropriate list of countries
  let countries = showAllCountries ? getAllCountries() : getSupportedCountries();
  
  // If specific supported services are requested, filter the list further
  if (supportedServices && supportedServices.length > 0) {
    countries = countries.filter(country => 
      supportedServices.some(service => isServiceAvailable(country.code, service))
    );
  }

  // Enhanced country options with service indicators
  const renderCountryOptions = () => {
    return countries.map((country) => {
      // Check which services are available for this country
      const availableServices: Array<keyof typeof SUPPORTED_SERVICES> = [];
      Object.keys(SUPPORTED_SERVICES).forEach((service) => {
        const serviceKey = service as keyof typeof SUPPORTED_SERVICES;
        if (isServiceAvailable(country.code, serviceKey)) {
          availableServices.push(serviceKey);
        }
      });

      // Format country name with service indicators if enabled
      const countryName = t(`countries.${country.name}`, country.name);
      let optionText = countryName;

      if (showServiceIndicators && availableServices.length > 0) {
        optionText = `${countryName} (${availableServices.map(s => 
          s === 'STRIPE' ? 'Stripe' : s
        ).join(', ')})`;
      }

      return (
        <option key={country.code} value={country.code} data-services={availableServices.join(',')}>
          {optionText}
        </option>
      );
    });
  };
  
  return (
    <div className="mb-4">
      {label && (
        <label htmlFor={id} className="form-label fw-medium">
          {label}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      )}
      
      <select 
        id={id}
        className={selectClasses}
        value={value}
        onChange={onChange}
        disabled={disabled}
        aria-invalid={!!error}
        aria-describedby={`${id}-feedback ${id}-help`}
        required={required}
      >
        <option value="">{placeholder || t('withdrawals.selectCountryOption', 'Select your country')}</option>
        {renderCountryOptions()}
      </select>
      
      {error && (
        <div id={`${id}-feedback`} className="invalid-feedback">
          {error}
        </div>
      )}
      
      {helpText && (
        <div id={`${id}-help`} className="form-text">
          {helpText}
        </div>
      )}
    </div>
  );
};

export default CountrySelector; 